import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { Box, Typography, CircularProgress } from '@mui/material';
import { db } from '../firebaseApp';
import GlassCard from './GlassCard.jsx';
import ActionButton from './ActionButton.jsx';
import useDocumentTitle from '../utils/useDocumentTitle';
import { PALETTE } from '../theme';

const PAGE_BG = 'linear-gradient(135deg, #faf6f0 0%, #f3e4d1 100%)';

const Shell = ({ children }) => (
    <Box
        sx={{
            minHeight: '100vh',
            background: PAGE_BG,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
        }}
    >
        {children}
    </Box>
);

const goHome = () => {
    window.location.href = '/';
};

const SharePage = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [record, setRecord] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'shares', id));
                if (active) {
                    setRecord(snap.exists() ? snap.data() : null);
                    setLoading(false);
                }
            } catch (err) {
                console.error('Failed to load shared map:', err);
                if (active) {
                    setRecord(null);
                    setLoading(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, [id]);

    useDocumentTitle(record ? `${record.title || 'Shared map'} | Map Shaper` : 'Shared map | Map Shaper');

    if (loading) {
        return (
            <Shell>
                <CircularProgress sx={{ color: PALETTE.primary }} />
            </Shell>
        );
    }

    if (!record) {
        return (
            <Shell>
                <GlassCard sx={{ p: 4, textAlign: 'center', maxWidth: 460, width: '100%' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: PALETTE.text, mb: 1 }}>
                        Map not found
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                        This shared map link is invalid or no longer available.
                    </Typography>
                    <ActionButton variant="primary" onClick={goHome}>
                        Go to Map Shaper
                    </ActionButton>
                </GlassCard>
            </Shell>
        );
    }

    return (
        <Shell>
            <GlassCard
                sx={{
                    p: { xs: 2.5, sm: 4 },
                    textAlign: 'center',
                    maxWidth: 680,
                    width: '100%',
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 'bold', color: PALETTE.text, mb: 0.5, lineHeight: 1.25 }}
                >
                    🗺️ {record.title || 'A travel map'}
                </Typography>
                <Typography variant="body2" sx={{ color: PALETTE.textMuted, mb: 2.5 }}>
                    Shared from map-shaped.com
                </Typography>
                <Box
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                        mb: 3,
                        backgroundColor: '#ffffff',
                    }}
                >
                    <img
                        src={record.image}
                        alt={record.title || 'Shared map'}
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                    />
                </Box>
                <ActionButton variant="primary" onClick={goHome}>
                    Create your own travel map
                </ActionButton>
            </GlassCard>
        </Shell>
    );
};

export default SharePage;
